import { config } from "../../config";
import { networkLayer } from "../../api/networkLayer";
import {
  AnonymousHolterUploadIdentifier,
  AnonymousUploadPart,
  AnonymousSmallFileUploadUrlResponse,
  UploadAnonymousHolterFileV2Options,
} from "./types";

export const buildIdentifierPayload = ({ serialNumber, sharableCode, measurementId }: AnonymousHolterUploadIdentifier) => {
  const measurementPayload = measurementId ? { measurement_id: measurementId } : {};

  if (serialNumber) return { serial_number: serialNumber, ...measurementPayload };
  if (sharableCode) return { sharable_code: sharableCode, ...measurementPayload };

  throw new Error("serialNumber or sharableCode is required");
};

export const initAnonymousHolterUploadV2 = async ({
  serialNumber,
  sharableCode,
  measurementId,
  file,
  origin,
}: UploadAnonymousHolterFileV2Options) => {
  return await networkLayer(
    "post",
    `${config.dataUrls}/measurement/holter/anonymous/v2/temporal_credentials/`,
    {
      ...buildIdentifierPayload({ serialNumber, sharableCode, measurementId }),
      file_size: file.size,
      origin: origin || window.location.origin,
    }
  );
};

export const requestAnonymousSmallFileUploadUrlV2 = async ({
  serialNumber,
  sharableCode,
  measurementId,
  filename,
}: AnonymousHolterUploadIdentifier & { filename: string }) => {
  return await networkLayer(
    "post",
    `${config.dataUrls}/measurement/holter/anonymous/v2/small_file_upload_url/`,
    {
    ...buildIdentifierPayload({ serialNumber, sharableCode, measurementId }),
      filename,
    }
  ) as { data: AnonymousSmallFileUploadUrlResponse };
};

export const listAnonymousHolterMultipartLinksV2 = async ({
  serialNumber,
  sharableCode,
  measurementId,
  uploadId,
  chunksNumber,
}: AnonymousHolterUploadIdentifier & { uploadId: string; chunksNumber: number }) => {
  return await networkLayer(
    "post",
    `${config.dataUrls}/measurement/holter/anonymous/v2/list_multipart_links/`,
    {
      ...buildIdentifierPayload({ serialNumber, sharableCode, measurementId }),
      upload_id: uploadId,
      chunks_number: chunksNumber,
    }
  );
};

export const finishAnonymousHolterUploadV2 = async ({
  serialNumber,
  sharableCode,
  measurementId,
  uploadId,
  chunksNumber,
  parts,
  fileSize,
}: AnonymousHolterUploadIdentifier & {
  uploadId: string;
  chunksNumber?: number;
  parts?: AnonymousUploadPart[];
  fileSize: number;
}) => {
  const response = await networkLayer(
    "post",
    `${config.dataUrls}/measurement/holter/anonymous/v2/finish_multipart_upload/`,
    {
      ...buildIdentifierPayload({ serialNumber, sharableCode, measurementId }),
      upload_id: uploadId,
      file_size: fileSize,
      ...(chunksNumber ? { chunks_number: chunksNumber } : {}),
      ...(parts ? { parts } : {}),
    }
  );

  const responseData = (response as { data?: { measurement_id?: string } } | undefined)?.data;

  if (!response || typeof response !== "object" || (response as { error?: unknown }).error || !responseData) {
    throw new Error("Could not confirm anonymous Holter upload");
  }

  if (measurementId && responseData.measurement_id !== measurementId) {
    throw new Error("Anonymous Holter upload was confirmed for a different measurement");
  }

  return response;
};

export const completeAnonymousHolterUploadV2 = async ({
  serialNumber,
  sharableCode,
  measurementId,
  fileSize,
}: AnonymousHolterUploadIdentifier & { fileSize: number }) => {
  const response = await networkLayer(
    "post",
    `${config.dataUrls}/measurement/holter/anonymous/v2/complete_upload/`,
    {
      ...buildIdentifierPayload({ serialNumber, sharableCode, measurementId }),
      file_size: fileSize,
    }
  );
  const responseData = (response as { data?: { measurement_id?: string } } | undefined)?.data;

  if (!response || typeof response !== "object" || (response as { error?: unknown }).error || !responseData) {
    throw new Error("Could not mark the recording as uploaded");
  }

  if (measurementId && responseData.measurement_id !== measurementId) {
    throw new Error("Anonymous Holter upload was confirmed for a different measurement");
  }

  return response;
};
